import { useEffect, useMemo, type PropsWithChildren, type FC, type Ref } from "react";
import { useTheme } from "@/hooks/use-theme";
import { GetStartedWithTasks } from "@/routes/ona-onboarding/GetStartedWithTasks";
import { GetStartedWithTasksShowcase } from "@/routes/ona-onboarding/GetStartedWithTasksShowcase";
import { STEP_COLOR_THEMES } from "@/routes/ona-onboarding/onboarding-color-themes";
import {
    currentStepAtom,
    type OnboardingStepSlug,
    ONBOARDING_STEP_SLUG,
    environmentClassAtom,
    scmAuthStatusesAtom,
    useSaveOnboardingStateEffect,
    ONBOARDING_STEP,
    onboardingRunnerAtom,
    type ScmAuthStatusMap,
    lastSelectedScmAuthHostAtom,
} from "@/routes/ona-onboarding/onboarding-state";
import { useQuery } from "@tanstack/react-query";
import { autoPaginate } from "@/queries/pagination";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useEnsureOnboardingRunner, useCheckAuthForAllScmIntegrations } from "@/queries/runner-queries";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { useSegmentTrackError } from "@/hooks/use-segment";
import { formatError } from "@/utils/errors";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ConnectToScm } from "@/routes/ona-onboarding/ConnectToScm";
import { ConnectToScmShowcase } from "@/routes/ona-onboarding/ConnectToScmShowcase";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { useNavigate } from "react-router-dom";
import { SelectRepository } from "@/routes/ona-onboarding/SelectRepository";
import { SelectRepositoryShowcase } from "@/routes/ona-onboarding/SelectRepositoryShowcase";
import { OnboardingLayout } from "@/routes/ona-onboarding/OnboardingLayout";

const STEP_COMPONENTS = {
    [ONBOARDING_STEP_SLUG.getStartedWithTasks]: {
        content: () => <GetStartedWithTasks />,
        showcase: (ref: Ref<HTMLDivElement>) => <GetStartedWithTasksShowcase ref={ref} />,
    },
    [ONBOARDING_STEP_SLUG.connectToScm]: {
        content: () => <ConnectToScm />,
        showcase: (ref: Ref<HTMLDivElement>) => <ConnectToScmShowcase ref={ref} />,
    },
    [ONBOARDING_STEP_SLUG.selectRepository]: {
        content: () => <SelectRepository />,
        showcase: (ref: Ref<HTMLDivElement>) => <SelectRepositoryShowcase ref={ref} />,
    },
};

export const OnaOnboardingPage: FC<{ step: OnboardingStepSlug }> = ({ step: stepSlug }) => {
    const setCurrentStep = useSetAtom(currentStepAtom);
    const navigate = useNavigate();
    const store = useStore();
    const { effectiveTheme } = useTheme();

    useEffect(() => {
        const currentStep = store.get(currentStepAtom);
        const stepFromProp = ONBOARDING_STEP[stepSlug];
        if (stepFromProp.index < currentStep.index) {
            // user is going back to a previous step, we need to update the current step
            setCurrentStep(stepFromProp);
        } else if (stepFromProp.index > currentStep.index) {
            // user is trying to skip a step, we need to update the current step
            navigate(`/onboarding/ona/${currentStep.slug}`, { replace: true });
        }
    }, [stepSlug, navigate, setCurrentStep, store]);

    const { content: StepContent, showcase: renderShowcase } = STEP_COMPONENTS[stepSlug];

    const stepColorTheme = useMemo(() => {
        const variants = STEP_COLOR_THEMES[stepSlug];
        return effectiveTheme === "dark" ? variants.dark : variants.light;
    }, [stepSlug, effectiveTheme]);

    return (
        <BackgroundOnboardingSetup>
            <OnboardingLayout renderShowcase={renderShowcase} stepKey={stepSlug} stepColorTheme={stepColorTheme}>
                <StepContent />
            </OnboardingLayout>
        </BackgroundOnboardingSetup>
    );
};

const BackgroundOnboardingSetup: FC<PropsWithChildren> = ({ children }) => {
    useSaveOnboardingStateEffect();

    const api = useGitpodAPI();
    const setOnboardingEnvironmentClass = useSetAtom(environmentClassAtom);
    const setOnboardingRunner = useSetAtom(onboardingRunnerAtom);
    const setScmAuthStatuses = useSetAtom(scmAuthStatusesAtom);
    const lastSelectedScmAuthHost = useAtomValue(lastSelectedScmAuthHostAtom);
    const trackError = useSegmentTrackError();
    const { toast } = useToast();

    const {
        data: runnerToUse,
        isLoading: isLoadingRunner,
        error: runnerToUseError,
    } = useEnsureOnboardingRunner({ enabled: true });
    const {
        data: environmentClassForOnboarding,
        error: environmentClassForOnboardingQueryError,
        status: environmentClassForOnboardingStatus,
    } = useQuery({
        queryKey: ["environment-classes", "onboarding", runnerToUse?.runnerId],
        queryFn: async (): Promise<EnvironmentClassEntry> => {
            if (!runnerToUse) {
                throw new Error("Failed to set up a runner");
            }

            const environmentClasses = await autoPaginate(
                (params) => api.environmentService.listEnvironmentClasses(params),
                {
                    filter: {
                        runnerIds: [runnerToUse.runnerId],
                    },
                },
                (response) => response.environmentClasses,
            );

            let entry: EnvironmentClassEntry | null = null;
            // the api currently does not have a reliable mechanism for ordering the environment classes by size
            // we have to rely on the display name to find the smallest environment class
            for (const clazz of environmentClasses) {
                if (clazz.displayName.toLowerCase().includes("small")) {
                    entry = {
                        clazz,
                        runner: runnerToUse,
                        localRunner: false,
                        policyRestricted: false,
                    };
                }
            }

            // if we didn't find a small environment class, we'll use the first one
            if (!entry && environmentClasses.length > 0) {
                const firstClass = environmentClasses[0];
                entry = {
                    clazz: firstClass,
                    runner: runnerToUse,
                    localRunner: false,
                    policyRestricted: false,
                };
            }

            if (!entry) {
                throw new Error("No environment class available");
            }

            return entry;
        },
        throwOnError: false,
        enabled: !!runnerToUse,
    });

    const { data: runnerSCMIntegrations } = useListRunnerSCMIntegrations(runnerToUse?.runnerId, {
        enabled: !!runnerToUse,
    });

    // Get relevant SCM integrations (GitHub, Bitbucket)
    const relevantIntegrations = useMemo(() => {
        if (!runnerSCMIntegrations) return [];
        return runnerSCMIntegrations
            .filter((integration) => integration.host === "github.com" || integration.host === "bitbucket.org")
            .map((integration) => integration.host);
    }, [runnerSCMIntegrations]);

    // Check auth for all relevant SCM integrations
    const authResults = useCheckAuthForAllScmIntegrations(runnerToUse?.runnerId, relevantIntegrations, {
        refetchConditions: {
            "github.com": lastSelectedScmAuthHost === "github.com",
            "bitbucket.org": lastSelectedScmAuthHost === "bitbucket.org",
        },
    });

    useEffect(() => {
        switch (environmentClassForOnboardingStatus) {
            case "pending":
                setOnboardingEnvironmentClass({ type: "pending" });
                break;
            case "success":
                setOnboardingEnvironmentClass({ type: "success", data: environmentClassForOnboarding });
                break;
            case "error":
                setOnboardingEnvironmentClass({ type: "error", error: environmentClassForOnboardingQueryError });
                toast({
                    title: "Failed to find an environment class",
                    description: formatError(environmentClassForOnboardingQueryError),
                });
                trackError("onboarding_find_environment_class_error", environmentClassForOnboardingQueryError);
                console.error(
                    "failed to find an environment class for onboarding",
                    environmentClassForOnboardingQueryError,
                );
                break;
        }
    }, [
        environmentClassForOnboarding,
        environmentClassForOnboardingQueryError,
        environmentClassForOnboardingStatus,
        setOnboardingEnvironmentClass,
        toast,
        trackError,
    ]);

    // Update scmAuthStatuses atom with all auth check results
    useEffect(() => {
        const statusMap: ScmAuthStatusMap = {};
        let firstError: unknown = null;

        for (const [host, result] of Object.entries(authResults)) {
            switch (result.status) {
                case "pending":
                    statusMap[host] = { type: "pending" };
                    break;
                case "success":
                    statusMap[host] = { type: "success", data: result.data };
                    break;
                case "error":
                    statusMap[host] = { type: "error", error: result.error };
                    if (!firstError) {
                        firstError = result.error;
                    }
            }
        }

        setScmAuthStatuses(statusMap);

        // Show error toast only once if any auth check fails
        if (firstError) {
            toast({
                title: "Failed to check SCM authentication",
                description: formatError(firstError),
            });
            trackError("onboarding_check_scm_auth_error", firstError);
            console.error("failed to check scm auth", firstError);
        }
    }, [authResults, setScmAuthStatuses, toast, trackError]);

    useEffect(() => {
        if (runnerToUse) {
            setOnboardingRunner({ type: "success", data: runnerToUse });
        } else if (isLoadingRunner) {
            setOnboardingRunner({ type: "pending" });
        } else if (runnerToUseError) {
            setOnboardingRunner({ type: "error", error: runnerToUseError });
            toast({
                title: "Failed to set up a runner",
                description: formatError(runnerToUseError),
            });
            trackError("onboarding_ensure_runner_error", runnerToUseError);
            console.error("failed to set up a runner", runnerToUseError);
        }
    }, [runnerToUse, isLoadingRunner, runnerToUseError, setOnboardingRunner, toast, trackError]);

    return children;
};
